// Selection as pure functions over SelectionState (types.js). Page-scoped selection ('rows', or 'page' when
// the header chose the whole eligible page) holds explicit row identities. Query-scoped selection ('all')
// stands for every eligible matching row and holds no keys; its count is the exact eligible matching count.

import { publishedSelection } from '../state/publish'
import {
  DEFAULT_INELIGIBLE,
  FAILURE_TEXT,
  partialText,
  successText,
} from './copy'

/** @returns {import('../types').SelectionState} */
export const noSelection = () => ({
  scope: 'none',
  ids: new Set(),
  excluded: new Set(),
})

export const rowId = (config, row) => String(row[config.rowKey])

/** Rows are eligible unless the instance's rule says false, which is how the eligible count reads it. */
export const isEligible = (config, row) => {
  const rule = config.selection && config.selection.isEligible
  return typeof rule !== 'function' || rule(row) !== false
}

export const ineligibleReason = (config, row) => {
  const reason = config.selection && config.selection.ineligibleReason
  const text = typeof reason === 'function' ? reason(row) : reason
  return typeof text === 'string' && text.trim() ? text : DEFAULT_INELIGIBLE
}

export const isQueryScoped = selection =>
  Boolean(selection) && selection.scope === 'all'

export const isActive = selection =>
  Boolean(selection) &&
  (selection.scope === 'all' ||
    ((selection.scope === 'rows' || selection.scope === 'page') &&
      selection.ids.size > 0))

/** Whether the row is part of the selection; an ineligible row never is. */
export const isSelected = (config, selection, id, row) => {
  if (!config.selection || !selection || !row || !isEligible(config, row))
    return false
  if (selection.scope === 'all')
    return !(selection.excluded && selection.excluded.has(id))
  if (selection.scope === 'rows' || selection.scope === 'page')
    return Boolean(selection.ids && selection.ids.has(id))
  return false
}

/** The identities of the eligible rows among `rows`, in order. */
export const eligibleIds = (config, rows) =>
  rows.filter(row => isEligible(config, row)).map(row => rowId(config, row))

const explicit = (ids, scope = 'rows') => ({
  scope: ids.size ? scope : 'none',
  ids,
  excluded: new Set(),
})

/** Selects or deselects one row. */
export const toggleRow = (selection, id) => {
  const ids = new Set(selection.scope === 'all' ? [] : selection.ids)
  if (ids.has(id)) ids.delete(id)
  else ids.add(id)
  return explicit(ids)
}

/** Adds every eligible row from index `from` to index `to` of `rows`, inclusive, in either direction. */
export const selectRange = (config, selection, rows, from, to) => {
  const ids = new Set(selection.scope === 'all' ? [] : selection.ids)
  const start = Math.max(0, Math.min(from, to))
  const end = Math.min(rows.length - 1, Math.max(from, to))
  for (let index = start; index <= end; index += 1)
    if (isEligible(config, rows[index])) ids.add(rowId(config, rows[index]))
  return explicit(ids)
}

/**
 * The header checkbox over the page's eligible identities: checked when every one is selected, mixed when
 * some are, locked while the selection is query-scoped.
 */
export const headerState = (selection, pageIds) => {
  if (isQueryScoped(selection))
    return { checked: pageIds.length > 0, mixed: false, locked: true }
  const chosen = pageIds.filter(id => selection.ids.has(id)).length
  const checked = pageIds.length > 0 && chosen === pageIds.length
  return { checked, mixed: chosen > 0 && !checked, locked: false }
}

/** Selects the page's eligible rows, or deselects them when every one is already selected. */
export const togglePage = (selection, pageIds) => {
  const { checked } = headerState(selection, pageIds)
  const ids = new Set(selection.ids)
  pageIds.forEach(id => (checked ? ids.delete(id) : ids.add(id)))
  const whole = ids.size === pageIds.length && pageIds.every(id => ids.has(id))
  return explicit(ids, whole ? 'page' : 'rows')
}

export const escalate = () => ({
  scope: 'all',
  ids: new Set(),
  excluded: new Set(),
})

const known = count => Number.isInteger(count) && count >= 0

/** How many rows the selection holds. */
export const selectedCount = (selection, eligibleTotal) => {
  if (!selection) return 0
  if (isQueryScoped(selection))
    return known(eligibleTotal)
      ? Math.max(0, eligibleTotal - (selection.excluded || new Set()).size)
      : 0
  return isActive(selection) ? selection.ids.size : 0
}

/** A fully selected page offers the eligible matching set when it holds more rows than the page. */
export const canEscalate = (selection, pageIds, eligibleTotal) =>
  !isQueryScoped(selection) &&
  pageIds.length > 0 &&
  pageIds.every(id => selection.ids.has(id)) &&
  known(eligibleTotal) &&
  eligibleTotal > selection.ids.size

const freeze = value => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze)
    Object.freeze(value)
  }
  return value
}

/**
 * The immutable record bulk work runs against: the published selection shape plus its count. A page-scoped
 * selection carries its identities; a query-scoped one carries its query and eligible count, never keys.
 */
export const snapshotOf = (selection, query, eligibleTotal) =>
  freeze({
    ...publishedSelection(selection, query, eligibleTotal),
    count: selectedCount(selection, eligibleTotal),
  })

const count = value => (known(value) ? value : null)

/**
 * Reads what a bulk action's promise settled to. A rejection, or an outcome that completed nothing, is a
 * failure; an outcome with both completed and failed rows is partial; anything else is success.
 * @param {{status: 'fulfilled'|'rejected', value?: *}} settled
 * @param {number} total the snapshot's count
 * @returns {{kind: 'success'|'partial'|'failure', text: string}}
 */
export const outcomeOf = (settled, total) => {
  const value =
    settled.status === 'fulfilled' &&
    settled.value &&
    typeof settled.value === 'object'
      ? settled.value
      : {}
  const message =
    typeof value.message === 'string' && value.message.trim()
      ? value.message
      : null
  if (settled.status === 'rejected' || value.ok === false)
    return { kind: 'failure', text: message || FAILURE_TEXT }
  const failed = count(value.failed)
  const succeeded = count(value.succeeded)
  if (failed && failed > 0) {
    const done = succeeded === null ? Math.max(0, total - failed) : succeeded
    if (done === 0) return { kind: 'failure', text: message || FAILURE_TEXT }
    return {
      kind: 'partial',
      text: message || partialText(done, done + failed, failed),
    }
  }
  return {
    kind: 'success',
    text: message || successText(succeeded === null ? total : succeeded),
  }
}
